import React from 'react';
import DocumentPage, {
  Section,
  SubSection,
  Paragraph,
  BulletList,
  HazardTable,
} from './DocumentPage';

const ManualHandlingDoc = ({ onReturnHome }) => (
  <DocumentPage
    pageTitle="Manual Handling – RA & SSOW"
    documentHeading="Risk Assessment 02 & Safe System of Work: Manual Handling"
    meta={[
      ['Document Reference', 'PVS-RAMS-MH-001'],
      ['Version', '1.0'],
      ['Date of Issue', '17th March 2026'],
      ['Next Review Date', '17th March 2028'],
      ['Area / Location', 'Workshops / Yard areas – Across the site'],
      ['Activity', 'Manual Handling – moving items around the site and buildings'],
      ['Review Period', '24 months'],
    ]}
    acknowledgment={{
      documentName: 'Manual Handling – RA & SSOW',
      documentReference: 'PVS-RAMS-MH-001',
      processActivity: 'Manual Handling',
      versionNumber: '1.0',
      dateOfIssue: '17th March 2026',
    }}
    onReturnHome={onReturnHome}
  >
    <Section number="1" title="Introduction">
      <Paragraph>
        Manual handling is one of the most common causes of injury at work and leads to around a
        quarter of all workplace injuries. These include work related Musculoskeletal Disorders
        (MSDs) such as upper and lower limb pain, joint and repetitive strain injuries. Other injuries
        caused by manual handling can include fractures, trapped nerves, cuts and abrasions, hernias
        and damage to muscles, ligaments and tendons.
      </Paragraph>
      <Paragraph className="mt-4">
        A number of factors can cause injury including the weight of an item, the number of times you
        have to pick up or carry an item, the distance you are carrying it, the height you are
        picking it up from or putting it down at, and any twisting, bending, stretching or other
        awkward posture whilst undertaking a task.
      </Paragraph>
      <Paragraph className="mt-4">
        To help prevent injury, you should try to avoid manual handling tasks as far as practical.
        Your employer has provided handling aids such as: sack barrows and pallet trucks, roll cages,
        jacks and trolleys. Keep handling aids sensibly loaded, carry out a pre-use check of any
        equipment and report defects. Only use equipment if you have been trained to do so.
      </Paragraph>
    </Section>

    <Section number="2" title="Hazard Identification and Risk Assessment">
      <HazardTable
        columns={['Hazard Category', 'Hazards', 'Control Measures Already in Place', 'Additional Control Measures Required']}
        rows={[
          [
            'Manual Handling',
            [
              'Musculoskeletal disorders from lifting, lowering, pushing, pulling or carrying load(s).',
              'Cuts and lacerations to fingers, hands etc. from contact with sharp or jagged metal, broken glass etc.',
              'Damage to feet, legs by dropping load(s).',
            ],
            [
              'No repetitive manual handling activities take place (no production line, etc).',
              'Mechanical aids are provided: Forklift truck, Trollies, Sack carts, Hoists to move tanks, Rail systems.',
              'Personal protective equipment (PPE) is provided e.g. safety footwear and handling gloves for relevant staff.',
            ],
            'Ensure that staff have received manual handling awareness training using the SSOW document and that records of this are kept.',
          ],
        ]}
      />
    </Section>

    <Section number="3" title="Safe System of Work">
      <SubSection title="TILE – Prior to Undertaking any Manual Handling Activity">
        <Paragraph>
          <strong>T for Task:</strong> Does the activity involve body movements such as twisting,
          stooping or reaching upwards? Are you lifting, pushing or pulling? Are you travelling a long
          distance with the item?
        </Paragraph>
        <Paragraph className="mt-3">
          <strong>I for Individual:</strong> Are you capable of performing the task safely? Do you
          have any physical limitations? Are you suffering from a physical injury, a medical
          condition or are you pregnant? Are you wearing appropriate clothing and footwear?
        </Paragraph>
        <Paragraph className="mt-3">
          <strong>L for Load:</strong> How heavy is the item? Is it large or bulky? Is it difficult
          to grasp? Is it too large for you to see over? Will its contents shift?
        </Paragraph>
        <Paragraph className="mt-3">
          <strong>E for Environment:</strong> Is the route clear of obstructions? Is the flooring
          level and in good condition?
        </Paragraph>
        <Paragraph className="mt-3 font-semibold">
          Only lift, carry, move, pull or push what you can easily manage. There is no minimum or
          maximum weight – it depends on what you as an individual can personally manage.
        </Paragraph>
      </SubSection>

      <SubSection title="Good Handling and Lifting Technique">
        <BulletList
          items={[
            'Do think before lifting and handling. Plan the lift. Can handling aids be used? Where is the load going to be placed? Will help be needed? Remove obstructions.',
            'Do keep the load close to the waist for as long as possible. Keep the heaviest side of the load next to the body.',
            'Do adopt a stable position. Feet apart with one leg slightly forward. Be prepared to move your feet to maintain stability. Avoid tight clothing or unsuitable footwear.',
            'Do get a good hold. Hug the load as close as possible to the body.',
            'Do start in a good posture. A slight bending of the back, hips and knees is preferable to fully flexing the back (stooping) or fully flexing the hips and knees (squatting).',
            'Do avoid twisting the back or leaning sideways especially while the back is bent. Turning by moving the feet is better than twisting and lifting at the same time.',
            'Do keep the head up when handling. Look ahead, not down at the load, once held securely.',
            'Do move smoothly. The load should not be jerked or snatched.',
            'Do ask for help with awkward or heavy loads.',
            'Do put the load down, then adjust. If precise positioning is necessary, put it down first, then slide it into the desired position.',
            "Don't flex the back any further while lifting.",
            "Don't lift or handle more than can be easily managed. Seek advice or get help if in doubt.",
            "Don't stoop – this puts unnecessary strain on your back, legs and arms.",
          ]}
        />
      </SubSection>

      <SubSection title="Pushing and Pulling">
        <BulletList
          items={[
            'Handle aids such as sack barrows, trolleys and pallet trucks should have handle heights between the shoulder and waist. Carry out pre-use checks.',
            'Force: the amount of force to move a load over a flat, level surface with a well-maintained handling aid is at least 2% of the load weight (e.g. 8 kg for a 400 kg load). Push rather than pull when possible, provided you can see over the load and control steering and stopping.',
            'Slopes: get help from another worker when negotiating a slope or ramp; pushing and pulling forces can be very high.',
            'Uneven surfaces: moving over soft or uneven surfaces requires higher forces (up to 10% of load weight on uneven ground).',
            'Stance and pace: keep your feet well away from the load and go no faster than walking speed.',
            'Team handling: ensure team members are a good match in height/strength; one person is in charge and co-ordinates movement; everyone understands their role; there is good communication; everyone has a good grip; there is sufficient space.',
          ]}
        />
      </SubSection>

      <SubSection title="HSE Guidelines">
        <Paragraph>
          There is no such thing as a completely 'safe' manual handling operation. Working within the
          HSE guidance diagram zones reduces risk. The guideline weights are reduced if handling is
          done with arms extended, or at high or low levels, as that is where injuries are most
          likely to happen. Guideline weights assume that the load is readily grasped with both hands
          and that the operation takes place in reasonable working conditions, with the lifter in a
          stable body position. State of health, age, and other factors affect an individual's
          physical capability – give consideration to the specific Task, Individual, Load,
          Environment and any other relevant factors.
        </Paragraph>
      </SubSection>
    </Section>

    <Section number="4" title="Reminder">
      <Paragraph className="font-semibold">
        IF YOU HAVE QUERIES PLEASE RAISE THESE WITH YOUR MANAGER OR SUPERVISOR – DO NOT PUT YOURSELF
        AT RISK OF AN INJURY.
      </Paragraph>
    </Section>
  </DocumentPage>
);

export default ManualHandlingDoc;
